// Bootstraps the hey-api fetch client used by every generated hook/sdk.
//
// - Request: prepend /api/proxy to the URL so the browser reaches the BFF,
//   which attaches Bearer + X-Workspace-Id from the iron-session cookie.
// - Response: parse application/problem+json into the existing ApiError
//   (matching the shape consumers already branch on — code, traceId, fieldErrors).
// - 401: force a login redirect, same as the old bffMutator did.
//
// Imported once from the root client Providers component; all generated hooks
// read from the singleton.

import { client } from "./generated/client.gen"
import { ApiError, type ProblemDetails } from "./errors"
import { log } from "@/lib/logger"

export function bootstrapApiClient() {
  client.setConfig({
    // Remove the default localhost baseUrl; the request interceptor below
    // routes every call through /api/proxy, so we don't need a base URL here.
    baseUrl: "",
    // Return just the parsed data (not { data, response, request }) so call
    // sites behave like the old bffMutator — they receive the payload directly.
    responseStyle: "data",
  })

  // Request interceptor: rewrite /api/v1/... → /api/proxy/api/v1/... so the
  // browser hits the BFF, which attaches Bearer + X-Workspace-Id from cookies.
  client.interceptors.request.use(async (request) => {
    const url = new URL(request.url, window.location.origin)
    const proxied = `/api/proxy${url.pathname}${url.search}`
    // Materialize body before re-wrapping. `new Request(url, request)` transfers
    // the original body as a ReadableStream, which iOS Safari / Brave can't
    // upload ("ReadableStream uploading is not supported"). Buffering the body
    // side-steps streaming entirely. GET/HEAD have no body to materialize.
    const hasBody = request.method !== "GET" && request.method !== "HEAD"
    const body = hasBody ? await request.arrayBuffer() : undefined
    return new Request(proxied, {
      method: request.method,
      headers: request.headers,
      body: body && body.byteLength ? body : undefined,
      credentials: request.credentials,
      cache: request.cache,
      redirect: request.redirect,
      referrer: request.referrer,
      referrerPolicy: request.referrerPolicy,
      integrity: request.integrity,
      keepalive: request.keepalive,
      signal: request.signal,
    })
  })

  // Response interceptor: parse Problem+JSON errors into ApiError and handle
  // 401 with a login redirect — same contract as the old bffMutator.
  client.interceptors.response.use(async (response, request) => {
    if (response.status === 401 && typeof window !== "undefined") {
      const currentPath = window.location.pathname + window.location.search
      const safe =
        currentPath.startsWith("/") && !currentPath.startsWith("//") ? currentPath : undefined
      window.location.href = safe
        ? `/login?callbackUrl=${encodeURIComponent(safe)}`
        : "/login"
      throw new ApiError("Session expired", 401, "unauthenticated")
    }

    if (!response.ok) {
      const contentType = response.headers.get("content-type") ?? ""
      let problem: ProblemDetails | undefined
      if (contentType.includes("application/problem+json")) {
        problem = (await response.clone().json()) as ProblemDetails
      }
      const err = new ApiError(
        problem?.title ?? problem?.detail ?? `Request failed with status ${response.status}`,
        response.status,
        problem?.code,
        problem,
      )
      // 4xx are usually expected (validation, auth, claim conflicts) — log
      // as warn so the Seq Error filter stays focused on real failures.
      // 5xx and unknown stay at error.
      const logEvent = response.status >= 500 ? "error" : "warn"
      if (logEvent === "error") {
        log.error("api_error", err, {
          url: request.url,
          method: request.method,
          status: response.status,
        })
      } else {
        log.warn("api_error", {
          url: request.url,
          method: request.method,
          status: response.status,
          code: problem?.code,
        })
      }
      throw err
    }

    return response
  })
}
